import { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import CaptionBuilder from '../CaptionBuilder';
import styles from './IptcPanel.module.css';

const DEBOUNCE_MS = 1500;

/**
 * Inner component that manages caption editing state.
 * Remounted via key={imageId} when the photo changes, which
 * naturally resets all local state without ref tricks.
 */
function IptcPanelInner({
  imageId,
  matchId,
  persons,
  captionOverride,
  captionChains,
  renderedCaption,
  orderedNames,
  keywords,
  onCaptionChange,
  onCaptionReset,
  onKeywordsChange,
  disabled,
  saving,
}) {
  const [saveStatus, setSaveStatus] = useState(null);
  const debounceRef = useRef(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [kwSaveStatus, setKwSaveStatus] = useState(null);

  // Determine if caption is currently overridden
  const isOverridden = Boolean(captionOverride);
  const displayCaption = captionOverride || renderedCaption || '';

  // Track local edits independently from props
  const [localEdit, setLocalEdit] = useState(null);

  // Show local edit if user is actively editing, otherwise show prop value
  const shownCaption = localEdit !== null ? localEdit : displayCaption;

  // Cleanup debounce on unmount (which also happens on photo switch via key)
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Debounced save
  const handleChange = useCallback((value) => {
    setLocalEdit(value);
    setSaveStatus('typing');

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      setSaveStatus('saving');
      try {
        await onCaptionChange(value);
        setLocalEdit(null);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }, DEBOUNCE_MS);
  }, [onCaptionChange]);

  const handleReset = async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLocalEdit(null);
    setSaveStatus('saving');
    try {
      await onCaptionReset();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleAddKeyword = useCallback(async (value) => {
    const trimmed = value.trim();
    if (!trimmed || !onKeywordsChange) return;
    if (keywords.includes(trimmed)) {
      setKeywordInput('');
      return;
    }
    const newKeywords = [...keywords, trimmed];
    setKeywordInput('');
    setKwSaveStatus('saving');
    try {
      await onKeywordsChange(newKeywords);
      setKwSaveStatus('saved');
      setTimeout(() => setKwSaveStatus(null), 2000);
    } catch {
      setKwSaveStatus('error');
      setTimeout(() => setKwSaveStatus(null), 3000);
    }
  }, [keywords, onKeywordsChange]);

  const handleRemoveKeyword = useCallback(async (keyword) => {
    if (!onKeywordsChange) return;
    const newKeywords = keywords.filter((k) => k !== keyword);
    setKwSaveStatus('saving');
    try {
      await onKeywordsChange(newKeywords);
      setKwSaveStatus('saved');
      setTimeout(() => setKwSaveStatus(null), 2000);
    } catch {
      setKwSaveStatus('error');
      setTimeout(() => setKwSaveStatus(null), 3000);
    }
  }, [keywords, onKeywordsChange]);

  const handleKeywordKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddKeyword(keywordInput);
    }
  }, [keywordInput, handleAddKeyword]);

  const statusText = {
    typing: '...',
    saving: 'Saving...',
    saved: 'Saved',
    error: 'Save failed',
  };

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Caption</span>
          {isOverridden && <span className={styles.badge}>overridden</span>}
          {saveStatus && (
            <span className={styles.saveStatus}>{statusText[saveStatus]}</span>
          )}
        </div>
        <textarea
          className={clsx(styles.textarea, isOverridden && styles.textareaOverridden)}
          value={shownCaption}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          rows={4}
          placeholder={renderedCaption ? 'Caption from template...' : 'No caption template configured'}
        />
        {isOverridden && (
          <button
            className={styles.resetBtn}
            onClick={handleReset}
            disabled={disabled || saving}
          >
            Reset to template
          </button>
        )}
      </div>

      <div className={styles.separator} />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Persons in image</span>
        </div>
        <div className={styles.pills}>
          {orderedNames.length === 0 ? (
            <span className={styles.emptyText}>No persons tagged</span>
          ) : (
            orderedNames
              .filter((n) => !n.softDeleted)
              .map((n) => (
                <span
                  key={n.person_id}
                  className={clsx(styles.pill, !n.face && styles.pillManual)}
                >
                  {n.displayName || n.label}
                </span>
              ))
          )}
        </div>
      </div>

      <div className={styles.separator} />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Keywords</span>
          {kwSaveStatus && (
            <span className={styles.saveStatus}>{statusText[kwSaveStatus]}</span>
          )}
        </div>
        <div className={styles.pills}>
          {keywords.length === 0 && !onKeywordsChange && (
            <span className={styles.emptyText}>No keywords</span>
          )}
          {keywords.map((kw) => (
            <span key={kw} className={styles.pill}>
              {kw}
              {onKeywordsChange && !disabled && (
                <button
                  className={styles.pillRemove}
                  onClick={() => handleRemoveKeyword(kw)}
                  title={`Remove "${kw}"`}
                >
                  x
                </button>
              )}
            </span>
          ))}
        </div>
        {onKeywordsChange && !disabled && (
          <input
            type="text"
            className={styles.keywordInput}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleKeywordKeyDown}
            onBlur={() => { if (keywordInput.trim()) handleAddKeyword(keywordInput); }}
            placeholder="Add keyword (Enter or comma)"
          />
        )}
      </div>

      <div className={styles.separator} />

      <CaptionBuilder
        imageId={imageId}
        matchId={matchId}
        persons={persons}
        orderedNames={orderedNames}
        captionOverride={captionOverride}
        captionChains={captionChains}
        renderedCaption={renderedCaption}
        existingKeywords={keywords}
        onCaptionChange={onCaptionChange}
        onKeywordsChange={onKeywordsChange}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * IPTC metadata panel for per-photo caption editing and person display.
 *
 * Uses key={imageId} to remount the inner component when the photo changes,
 * which cleanly resets local edit state and flushes pending debounces.
 */
export default function IptcPanel({ imageId, ...props }) {
  return <IptcPanelInner key={imageId} imageId={imageId} {...props} />;
}
